import MaskedInput, { Mask } from 'react-text-mask';
import styled from 'styled-components';
import colors from '../components/theme/colors';
import { CardThumb, cards } from '../components/Donee/components/common/constants';
import { ErrorResponse } from '../api';

export const isPPOError = (error: ErrorResponse | null | undefined) => {
    return error && error?.status === 1409;
};

export const StyledMaskedInput = styled(MaskedInput)`
    width: 100%;
    min-height: 45px;
    font-size: 16px;
    padding: 8px 15px;
    color: ${colors.brandDarkGray};
    font-family: Area Normal;
    outline: 0;
    border: 1px solid ${colors.utilityInputColor};
    background-color: #fff;
    border-radius: 10px;
    -moz-border-radius: 10px;
    -webkit-border-radius: 10px;
    -o-border-radius: 10px;
`;

const year = new Date().getFullYear().toString().substring(2);
export const checkExpiryDate = (expiry: string): boolean => {
    if (expiry.length !== 5) {
        return false;
    }
    const [mm, yy] = expiry.split('/');
    return mm !== '00' && mm <= '12' && yy >= year;
};

export const checkCVV = (cvv: string, thumb: CardThumb) => {
    return cvv.length === thumb.cvcLength[0];
};

const spaceRegEx = / /g;
export const isCardNumberValid = (cardNumber: string, cardThumb: CardThumb) =>
    cardThumb.length.includes(cardNumber.replace(spaceRegEx, '').length) &&
    cardThumb.pattern.test(cardNumber);

export const isValidCard = (
    cardNumber: string | undefined,
    expiry: string,
    cvv: string,
    cardThumb: CardThumb | undefined,
): boolean => {
    if (!cardThumb || !cardNumber) {
        return false;
    }
    return (
        isCardNumberValid(cardNumber, cardThumb) &&
        checkExpiryDate(expiry) &&
        checkCVV(cvv, cardThumb)
    );
};

export const checkCardType = (
    newNumber: string,
    useCards: CardThumb[] = cards,
) => {
    for (let i = 0; i < useCards.length; i++) {
        const card = useCards[i];
        if (card.pattern.test(newNumber)) {
            return card;
        }
    }
    return undefined;
};

export const validateCardExpiry = (newExpiration: string): boolean => {
    let prefix: string | number;
    let month: number;
    let year: number;

    if (newExpiration.length === 5) {
        month = parseInt(newExpiration.toString().substr(0, 2), 10);
        year = parseInt(newExpiration.toString().substr(3, 2), 10);
    } else if (newExpiration.length === 4) {
        month = parseInt(newExpiration.toString().substr(0, 2), 10);
        year = parseInt(newExpiration.toString().substr(2, 2), 10);
    } else {
        return false;
    }

    if (year.toString().length === 2) {
        prefix = new Date().getFullYear();
        prefix = prefix.toString().slice(0, 2);
        year = parseInt(prefix + year, 10);
    }
    if (month > 12) {
        return false;
    }
    const expiry = new Date(year, month, 1);
    const currentTime = new Date();
    expiry.setMonth(expiry.getMonth() - 1);
    expiry.setMonth(expiry.getMonth() + 1, 1);

    return expiry >= currentTime;
};

export const expiryDateMask = [/\d/, /\d/, '/', /\d/, /\d/];
export const cvvAmericanExpressMask = [/\d/, /\d/, /\d/, /\d/];
export const cvvMask = [/\d/, /\d/, /\d/, /\d/];
export const zipCodeMask = [/\d/, /\d/, /\d/, /\d/, /\d/];

// Builds a mask sized to the digits actually present (capped at maxLength),
// grouped by groupSizes (last size repeats once exhausted). Keeping the mask
// length in step with the input — rather than a fixed-length mask padded with
// trailing separators — avoids a react-text-mask quirk where backspacing
// stalls once the value ends on a literal separator with no digit after it.
const buildGroupedMask = (
    value: string,
    groupSizes: number[],
    maxLength: number,
): Mask => {
    const digits = (value || '').replace(/\D/g, '').slice(0, maxLength);
    const length = digits.length || groupSizes[0];
    const mask: (RegExp | string)[] = [];
    let groupIndex = 0;
    let countInGroup = 0;
    for (let i = 0; i < length; i++) {
        const groupSize = groupSizes[groupIndex] ?? groupSizes[groupSizes.length - 1];
        if (countInGroup === groupSize) {
            mask.push(' ');
            countInGroup = 0;
            groupIndex += 1;
        }
        mask.push(/\d/);
        countInGroup += 1;
    }
    return mask;
};

export const americanExpressMask = (value: string): Mask =>
    buildGroupedMask(value, [4, 6, 5], 15);

export const cardMask = (value: string): Mask =>
    buildGroupedMask(value, [4, 4, 4, 4], 16);

export const dinersClubMask = (value: string): Mask =>
    buildGroupedMask(value, [4, 6, 4], 14);

const unionPayMask = (value: string): Mask =>
    buildGroupedMask(value, [4], 19);

const jcbMask = (value: string): Mask => buildGroupedMask(value, [4], 19);

export const getCardsThumbFormat = (
    type?: string,
): ((value: string) => Mask) => {
    switch (type) {
        case 'american express':
            return americanExpressMask;
        case 'diners club':
            return dinersClubMask;
        case 'union pay':
            return unionPayMask;
        case 'jcb':
            return jcbMask;
        default:
            return cardMask;
    }
};
